/**
 * QR Code Generator Control
 * Generates real, scannable QR codes using qrcode.react library
 */

import React, { memo } from 'react';
import QRCode from 'qrcode.react';
import type { ControlRendererProps } from '../types';

interface QRCodeControlConfig {
  value?: string;
  size?: number;
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  darkColor?: string;
  lightColor?: string;
  includeMargin?: boolean;
  margin?: number;
  enableDownload?: boolean;
  downloadFormat?: 'png' | 'jpeg' | 'svg';
  logoUrl?: string;
  logoSize?: number;
}

const QRCodeControl = memo(({ control, value, onChange, mode }: ControlRendererProps) => {
  const config = (control.config as QRCodeControlConfig) || {};
  const size = config.size ?? 200;
  const errorCorrectionLevel = config.errorCorrectionLevel ?? 'M';
  const darkColor = config.darkColor ?? '#000000';
  const lightColor = config.lightColor ?? '#FFFFFF';
  const includeMargin = config.includeMargin ?? true;
  const margin = config.margin ?? 10;
  const enableDownload = config.enableDownload ?? true;

  // Use value prop first, then fall back to config.value
  const qrValue = value || config.value || '';

  // In view or preview mode, only show the QR code
  if (mode === 'view' || mode === 'preview') {
    if (!qrValue) {
      return <div style={{ color: '#999', fontSize: '14px' }}>No QR code data</div>;
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
        <div style={{ padding: margin, backgroundColor: lightColor, borderRadius: '8px' }}>
          <QRCode value={qrValue} size={size} level={errorCorrectionLevel} fgColor={darkColor} bgColor={lightColor} includeMargin={includeMargin} />
        </div>
      </div>
    );
  }

  // In edit/design mode, show input + preview
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
          {control.label || 'QR Code'}
        </label>
        <textarea
          value={qrValue}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Enter text or URL to encode"
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'monospace',
            minHeight: '60px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {qrValue && (
        <div style={{
          border: '1px solid #eee',
          padding: '16px',
          borderRadius: '4px',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#333' }}>QR Code Preview</h4>
          <div style={{ padding: margin, backgroundColor: lightColor, borderRadius: '8px' }}>
            <QRCode value={qrValue} size={size} level={errorCorrectionLevel} fgColor={darkColor} bgColor={lightColor} includeMargin={includeMargin} />
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
            ✅ Scannable with any QR reader
          </div>
        </div>
      )}

      {!qrValue && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f9f9f9',
          border: '2px dashed #ddd',
          borderRadius: '4px',
          textAlign: 'center',
          color: '#999',
          fontSize: '13px',
        }}>
          Enter text or URL above to generate QR code
        </div>
      )}
    </div>
  );
});

QRCodeControl.displayName = 'QRCodeControl';

export { QRCodeControl };
export type { QRCodeControlConfig };
